import type { ExpandedState, OnChangeFn, SortingState } from "@tanstack/react-table"
import type { RefObject } from "react"
import { useCallback } from "react"
import { useShallow } from "zustand/react/shallow"

import { usePayFileSearchStore } from "../stores/usePayFileSearchStore"
import type { PayFileSearchParams } from "../types/searchParams"

import type { PayFileListFieldsFragment } from "@/graphql/codegen/graphql"
import { useListPageBase } from "@/hooks/useListPageBase"

interface UsePayFileListReturn {
  payFiles: PayFileListFieldsFragment[]
  isFetching: boolean
  error: Error | undefined
  sorting: SortingState
  expanded: ExpandedState
  searchParams: PayFileSearchParams
  tableContainerRef: RefObject<HTMLDivElement>
  refetch: () => void
  handleScroll: () => void
  handleSortingChange: OnChangeFn<SortingState>
  handleExpandedChange: OnChangeFn<ExpandedState>
  handleFilterChange: <K extends keyof PayFileSearchParams>(key: K, value: PayFileSearchParams[K]) => void
  handleFiltersChange: (updates: Partial<PayFileSearchParams>) => void
  handleFilterRemove: (
    keyOrKeys: keyof PayFileSearchParams | (keyof PayFileSearchParams)[],
    value?: string | number
  ) => void
  handleFilterClear: () => void
}

const ROUTE = "/app/accounting/_layout/huntington-pay-files"

const FILTER_KEYS: (keyof PayFileSearchParams)[] = ["statuses", "type", "scheduledPayDateFrom", "scheduledPayDateTo"]
const PRESERVED_EMPTY_FILTER_KEYS: (keyof PayFileSearchParams)[] = ["statuses"]

export const usePayFileList = (): UsePayFileListReturn => {
  const store = usePayFileSearchStore(
    useShallow(({ data, fetching, error, executeSearch, loadMore, clearStore }) => ({
      data,
      fetching,
      error,
      executeSearch,
      loadMore,
      clearStore,
    }))
  )

  const {
    data: payFiles,
    isFetching,
    error,
    sorting,
    expanded,
    searchParams,
    tableContainerRef,
    refetch,
    handleScroll,
    handleSortingChange,
    handleExpandedChange,
    handleFilterChange,
    handleFiltersChange,
    handleFilterRemove,
  } = useListPageBase<PayFileListFieldsFragment, PayFileSearchParams>({
    route: ROUTE,
    store,
    filterKeys: FILTER_KEYS,
    preservedEmptyFilterKeys: PRESERVED_EMPTY_FILTER_KEYS,
  })

  // Clear All must leave no chip, so statuses is cleared to an empty selection rather than removed from the URL
  // (an absent value would bring the default Pending + Approved + Processing chips back).
  const handleFilterClear = useCallback(
    () =>
      handleFiltersChange(
        Object.fromEntries(
          FILTER_KEYS.map((key) => [key, key === "statuses" ? [] : undefined])
        ) as Partial<PayFileSearchParams>
      ),
    [handleFiltersChange]
  )

  return {
    payFiles,
    isFetching,
    error,
    sorting,
    expanded,
    searchParams,
    tableContainerRef,
    refetch,
    handleScroll,
    handleSortingChange,
    handleExpandedChange,
    handleFilterChange,
    handleFiltersChange,
    handleFilterRemove,
    handleFilterClear,
  }
}
